export const TIME_ZONE = 'America/Bogota';
export const LOCALE = 'es-CO';
export const DATE_PLACEHOLDER = 'Fecha no disponible';

const ISO_INSTANT_PATTERN = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/i;
const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const bogotaDateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
});

const bogotaDateFormatter = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric'
});

const bogotaTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
});

const calendarDateFormatter = new Intl.DateTimeFormat(LOCALE, {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
});

const bogotaDateInputFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
});

const partsByType = (formatter, value) => Object.fromEntries(
    formatter.formatToParts(value)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
);

const titleCase = (value = '') => value
    ? `${value.charAt(0).toLocaleUpperCase(LOCALE)}${value.slice(1).toLocaleLowerCase(LOCALE)}`
    : '';

const compactDayPeriod = (value = '') => value.toLocaleLowerCase(LOCALE).replace(/[\s.\u202f]/g, '');

const isValidCalendarParts = (year, month, day) => {
    const parsed = new Date(Date.UTC(year, month - 1, day, 12));
    return parsed.getUTCFullYear() === year
        && parsed.getUTCMonth() === month - 1
        && parsed.getUTCDate() === day;
};

/**
 * Parses only real instants. Date-only and timezone-less strings are rejected so
 * the browser or operating-system timezone can never silently change semantics.
 */
export const parseInstant = (value) => {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value !== 'string') return null;
    const normalized = value.trim();
    const match = normalized.match(ISO_INSTANT_PATTERN);
    if (!match) return null;

    const calendar = parseCalendarDate(match[1]);
    if (!calendar) return null;

    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const parseCalendarDate = (value) => {
    if (typeof value !== 'string') return null;
    const match = value.match(CALENDAR_DATE_PATTERN);
    if (!match) return null;

    const [, yearValue, monthValue, dayValue] = match;
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    if (!isValidCalendarParts(year, month, day)) return null;

    return { year, month, day, value };
};

export const formatBogotaDateTime = (value, placeholder = DATE_PLACEHOLDER) => {
    const parsed = parseInstant(value);
    if (!parsed) return placeholder;

    try {
        const parts = partsByType(bogotaDateTimeFormatter, parsed);
        return `${parts.day} ${titleCase(parts.month)} ${parts.year} - ${parts.hour}:${parts.minute} ${compactDayPeriod(parts.dayPeriod)}`;
    } catch {
        return placeholder;
    }
};

export const formatBogotaDate = (value, placeholder = DATE_PLACEHOLDER) => {
    const parsed = parseInstant(value);
    if (!parsed) return placeholder;

    try {
        const parts = partsByType(bogotaDateFormatter, parsed);
        return `${parts.day} ${titleCase(parts.month)} ${parts.year}`;
    } catch {
        return placeholder;
    }
};

export const formatBogotaTime = (value, placeholder = DATE_PLACEHOLDER) => {
    const parsed = parseInstant(value);
    if (!parsed) return placeholder;

    try {
        const parts = partsByType(bogotaTimeFormatter, parsed);
        return `${parts.hour}:${parts.minute} ${compactDayPeriod(parts.dayPeriod)}`;
    } catch {
        return placeholder;
    }
};

/**
 * Formats a business calendar date without applying a timezone conversion.
 */
export const formatCalendarDate = (value, placeholder = DATE_PLACEHOLDER) => {
    const calendar = parseCalendarDate(value);
    if (!calendar) return placeholder;

    try {
        const parsed = new Date(Date.UTC(calendar.year, calendar.month - 1, calendar.day, 12));
        const parts = partsByType(calendarDateFormatter, parsed);
        return `${parts.day} ${titleCase(parts.month)} ${parts.year}`;
    } catch {
        return placeholder;
    }
};

export const toDateInputValue = (value) => parseCalendarDate(value)?.value || '';

/**
 * Converts a real instant to the YYYY-MM-DD value that a Bogotá date input
 * should show. It does not reinterpret an existing calendar-date string.
 */
export const toBogotaDateInputValue = (value) => {
    const parsed = parseInstant(value);
    if (!parsed) return '';

    try {
        const parts = partsByType(bogotaDateInputFormatter, parsed);
        return `${parts.year}-${parts.month}-${parts.day}`;
    } catch {
        return '';
    }
};

// Backwards-compatible name used by the existing screens.
export const timeFormatter = formatBogotaDateTime;
